import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Botão "🏬 Loja" — troca de organização sem logout. Ao confirmar, recarrega
// a página — a organização muda o contexto de praticamente tudo (caixa,
// financeiro, estoque), então um reload garante que nenhuma tela fique com
// dados da loja anterior em memória.
//
// `variant`: 'header' (padrão, barra branca no topo) ou 'sidebar' (menu
// lateral escuro, embaixo da logo). O dropdown é renderizado num portal —
// a sidebar tem `overflow:hidden` (evita vazar conteúdo/scroll horizontal),
// que cortaria a lista de organizações se ela nascesse dentro da sidebar.
export default function SeletorOrganizacao({ variant = 'header' }) {
  const { usuario, trocarOrganizacao } = useAuth();
  const [aberto, setAberto] = useState(false);
  const [orgs, setOrgs] = useState(null);
  const [trocando, setTrocando] = useState(false);
  const [erro, setErro] = useState('');
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);

  // Carrega uma vez ao montar: decide se o botão aparece (só p/ usuário com
  // mais de uma loja) sem precisar abrir o dropdown primeiro.
  useEffect(() => {
    api.get('/auth/minhas-organizacoes')
      .then(({ data }) => setOrgs(data))
      .catch(() => setOrgs([]));
  }, []);

  useEffect(() => {
    // Dropdown vive num portal (fora da sidebar) — o clique nele não é mais
    // descendente de wrapRef, então precisa do atributo pra não fechar sozinho.
    const onClick = e => {
      if (wrapRef.current?.contains(e.target)) return;
      if (e.target.closest?.('[data-seletor-org-dropdown]')) return;
      setAberto(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = () => {
    if (!aberto && wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setPos(variant === 'sidebar'
        ? { top: r.bottom + 4, left: r.left, width: Math.max(260, r.width) }
        : { top: r.bottom + 4, right: window.innerWidth - r.right, width: 260 });
    }
    setAberto(v => !v);
  };

  const escolher = async (org) => {
    if (org.atual || trocando) return;
    setTrocando(true);
    setErro('');
    try {
      await trocarOrganizacao(org.id);
      window.location.reload();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao trocar de organização.');
      setTrocando(false);
    }
  };

  // Só um usuário com mais de uma organização precisa do seletor
  if (orgs !== null && orgs.length <= 1) return null;

  const sidebar = variant === 'sidebar';

  return (
    <div ref={wrapRef} style={sidebar ? { padding: '0 14px 12px' } : { position: 'relative' }}>
      <button
        onClick={toggle}
        title="Trocar de loja/organização sem sair do sistema — a troca vale só para ESTA guia (abra outra guia para operar as duas empresas ao mesmo tempo)"
        style={sidebar ? {
          display: 'flex', alignItems: 'center', gap: 7, width: '100%', height: 34, padding: '0 10px',
          border: '1px solid var(--sidebar-border)', borderRadius: 6, background: 'rgba(255,255,255,0.06)',
          cursor: 'pointer', fontSize: 12.5, color: 'var(--sidebar-text-active, #fff)',
        } : {
          display: 'flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px',
          border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer',
          fontSize: 13, color: '#1f2937', maxWidth: 200,
        }}>
        <span>🏬</span>
        <span style={{ flex: sidebar ? 1 : undefined, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {usuario?.organizacao_nome || 'Loja'}
        </span>
        <span style={{ fontSize: 10, opacity: .6 }}>▾</span>
      </button>

      {aberto && pos && createPortal(
        <div data-seletor-org-dropdown style={{
          position: 'fixed', top: pos.top, left: pos.left, right: pos.right, width: pos.width,
          zIndex: 2000, background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
          boxShadow: '0 8px 24px rgba(0,0,0,.15)', overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#374151',
            background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            Trocar de loja
          </div>
          {erro && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: '#b91c1c' }}>{erro}</div>
          )}
          {orgs === null && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: '#888' }}>Carregando…</div>
          )}
          {orgs?.map(org => (
            <div key={org.id}
              onClick={() => escolher(org)}
              style={{
                padding: '9px 12px', cursor: org.atual ? 'default' : 'pointer', fontSize: 13,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: org.atual ? '#eff6ff' : '#fff',
                opacity: trocando ? .6 : 1,
              }}
              onMouseEnter={e => { if (!org.atual) e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={e => { e.currentTarget.style.background = org.atual ? '#eff6ff' : '#fff'; }}>
              <div>
                <div style={{ fontWeight: org.atual ? 700 : 400 }}>
                  {org.nome_fantasia || org.razao_social}
                </div>
                {org.cnpj && <div style={{ fontSize: 11, color: '#888' }}>{org.cnpj}</div>}
              </div>
              {org.atual && <span style={{ color: '#1565c0', fontSize: 12, fontWeight: 700 }}>✓ atual</span>}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
